import type { QueryExecuteOptions, QueryKey, UseMutationOptions } from '@tanstack/react-query';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import type { ProblemDetail } from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import type { EditableFieldValue } from '../../components/EditableEntityTable';
import { runMutation } from './runMutation';

export type EntityFieldBase<TBody extends object = object, TItem extends object = object> = {
  /** Property path on the entity; dots address nested properties, e.g. `auditBean.created`. */
  key: string;
  label: string;
  /** Shown but never editable, even if the schema has an edit endpoint. */
  readOnly?: boolean;
  /**
   * Converts the edited value into the fragment of the edit request body that sets this field.
   * A field without one is not editable.
   */
  convertValue?: (value: EditableFieldValue) => Partial<TBody>;
  /**
   * Converts the edited value into the fragment of the entity that sets this field, for entities
   * edited as rows of a related list. A field without one can't be edited there.
   */
  convertItemValue?: (value: EditableFieldValue) => Partial<TItem>;
  /** Formats the field for display from the whole entity, e.g. to summarize a list. */
  // Method syntax keeps fields of any entity assignable to the untyped `EntityField`.
  display?(entity: TItem): string;
};

export type EntityField<TBody extends object = object, TItem extends object = object> =
  | (EntityFieldBase<TBody, TItem> & { type: 'string' })
  | (EntityFieldBase<TBody, TItem> & { type: 'textarea' })
  | (EntityFieldBase<TBody, TItem> & { type: 'number'; integer?: boolean })
  | (EntityFieldBase<TBody, TItem> & { type: 'boolean' })
  | (EntityFieldBase<TBody, TItem> & { type: 'enum'; options: ReadonlyArray<string> })
  | (EntityFieldBase<TBody, TItem> & { type: 'datetime' });

/** Request of a generated list query; it may take more than the explorer's own search parameters. */
type QueryArgs = { query?: Partial<EntityQueryParams> };

export type EntityQueryParams = { search?: string; page: number; pageSize: number };

/** An entry in the Entity Explorer's list, already reduced to what the list shows. */
export type ListEntry = { id: string; primaryText: string; secondaryText?: string };

/** An entry of an entity found through a parent, with the entity itself so no read is needed. */
export type ParentListEntry<TEntity> = ListEntry & { item: TEntity };

export type ListPage = { entries: ListEntry[]; nextPage?: number };

export type EntityPage<TEntity> = { results?: TEntity[]; nextPage?: number };

type RequestBody<TVars> = TVars extends { body?: infer TBody extends object }
  ? NonNullable<TBody>
  : never;

/** The complete list of a parent's items as it should be saved, rows added or changed. */
type EditRow<TEntity> = { items: TEntity[] };

/**
 * Builds the edit request. Entities with an id are edited field by field: `id` is the entity's id,
 * `body` holds the edited field and `row` can be ignored. Entities without one are saved as a list
 * per parent: `id` is then the parent's id and `row` holds the list to save.
 */
type EditEndpoint<TData, TVars, TEntity = never> = {
  /** The generated SDK `XYZMutation` function. */
  mutation: () => UseMutationOptions<TData, ProblemDetail, TVars>;
  toRequest: (id: string, body: Partial<RequestBody<TVars>>, row: EditRow<TEntity>) => TVars;
  /** Starting values for a new row; enables adding and editing rows in related lists. */
  newItem?: () => TEntity;
};

/** Position of a row within its parent's complete list. */
type DeleteRow<TEntity> = { items: TEntity[]; index: number };

/**
 * Builds the delete request. Entities with an id are deleted by id and can ignore `row`. Entities
 * without one are saved as a list per parent: `id` is then the parent's id and `row` locates the
 * item to delete.
 */
type DeleteEndpoint<TData, TVars, TEntity = never> = {
  mutation: () => UseMutationOptions<TData, ProblemDetail, TVars>;
  toRequest: (id: string, row: DeleteRow<TEntity>) => TVars;
};

/** Every entity that can be explored; related lists link to one of these. */
export type EntityName =
  | 'compSet'
  | 'hotel'
  | 'organization'
  | 'organizationLimit'
  | 'organizationUser'
  | 'session'
  | 'user';

export type RelatedListField = {
  /** Property path on the item; dots address nested properties, e.g. `user.name`. */
  key: string;
  label: string;
  type?: 'string' | 'datetime';
};

/** Related list as consumed by the Entity Explorer. */
export type RelatedList = {
  label: string;
  fields: ReadonlyArray<RelatedListField>;
  /** Entity the rows link to in the explorer, when they have one. */
  linkEntity?: EntityName;
  /** Label of the entity each row represents, e.g. "Session". */
  entityLabel: string;
  /** Fetches the rows belonging to the parent entity with the given id. */
  fetch: (parentId: string) => Promise<RelatedListRow[]>;
  /** Present when rows can be added and edited. */
  edit?: RelatedListEdit;
  /** Present when rows can be deleted; takes the row's entity id. */
  delete?: (entityId: string) => Promise<unknown>;
  /** Present when rows without an id can be deleted, by saving the list with the row marked deleted. */
  deleteRow?: (parentId: string, index: number) => Promise<unknown>;
};

export type RelatedListEdit = {
  /** The entity's fields that can be edited as part of a row. */
  fields: ReadonlyArray<EntityField>;
  /** Starting values for a new row. */
  newItem: () => object;
  /** Adds a row, or replaces the row at `index`, and saves the resulting list for the parent. */
  save: (
    parentId: string,
    index: number | undefined,
    values: Readonly<Record<string, EditableFieldValue>>
  ) => Promise<unknown>;
};

export type RelatedListRow = {
  /** Id of the linked entity, when the item has one. */
  entityId?: string;
  item: object;
};

/**
 * Related list whose rows are read through the `related` endpoint of another entity schema.
 * Only `setParentId` is specific to the parent: it places the parent id in that endpoint's request.
 */
export function defineRelatedList<TEntity extends object, TArgs>(definition: {
  label: string;
  entity: EntitySchema<TEntity, TArgs>;
  /** Places the parent id in the right spot of the request. */
  setParentId: (id: string) => TArgs;
  /** Where rows link to. Defaults to the entity itself, using its `idField`. */
  link?: { entity: EntityName; getId: (item: TEntity) => string | undefined };
  fields: ReadonlyArray<RelatedListField>;
}): RelatedList {
  const { entity, setParentId, link } = definition;
  const { related, editRelated, delete: remove, deleteRelated } = entity.endpoints;
  if (!related) throw new Error(`Entity "${entity.name}" has no related endpoint.`);
  const { idField } = entity;
  const getId =
    link?.getId ??
    (idField ? (item: TEntity) => (item[idField] ? String(item[idField]) : undefined) : undefined);
  return {
    label: definition.label,
    fields: definition.fields,
    entityLabel: entity.label,
    linkEntity: link?.entity ?? (idField ? entity.name : undefined),
    fetch: async (parentId) =>
      (await related(setParentId(parentId))).map((item) => ({ entityId: getId?.(item), item })),
    delete: idField ? remove : undefined,
    deleteRow:
      !idField && deleteRelated
        ? async (parentId, index) =>
            deleteRelated(parentId, await related(setParentId(parentId)), index)
        : undefined,
    edit: editRelated && {
      fields: entity.fields.filter((field) => field.convertItemValue && !field.readOnly),
      newItem: editRelated.newItem,
      save: async (parentId, index, values) => {
        const items = await related(setParentId(parentId));
        let item = index === undefined ? editRelated.newItem() : items[index];
        if (!item) throw new Error('The row no longer exists.');
        for (const field of entity.fields) {
          if (field.readOnly || !field.convertItemValue || !(field.key in values)) continue;
          item = { ...item, ...field.convertItemValue(values[field.key]) };
        }
        const next =
          index === undefined
            ? [item, ...items]
            : items.map((old, i) => (i === index ? item : old));
        return editRelated.save(parentId, next);
      },
    },
  };
}

/** A property key, or a function for text composed from several properties. */
export type EntityText<TEntity> = (keyof TEntity & string) | ((entity: TEntity) => string);

type EntityFields<TEntity extends object, TBody extends object = object> = {
  name: EntityName;
  label: string;
  /** Property holding the entity id; omit when the entity has none. */
  idField?: keyof NoInfer<TEntity> & string;
  /**
   * When the entity type comes only from `endpoints.related.toItems`, write these as plain arrow
   * functions placed after `endpoints`; a property-name string or a destructured parameter makes
   * TypeScript settle the entity type before `toItems` is read.
   */
  primaryText: EntityText<NoInfer<TEntity>>;
  secondaryText?: EntityText<NoInfer<TEntity>>;
  fields: ReadonlyArray<EntityField<TBody, NoInfer<TEntity>>>;
  /** Related items shown, collapsed, under the entity detail. Each loads when first opened. */
  relatedLists?: ReadonlyArray<RelatedList>;
};

/**
 * Schema as authored: endpoints are the generated SDK functions, and every type (entity, query keys,
 * mutation data/variables) is inferred from them by `defineEntitySchema`.
 */
export type EntitySchemaDefinition<
  TQueryItem extends object,
  TQueryArgs extends QueryArgs,
  TQueryKey extends QueryKey,
  TReadKey extends QueryKey,
  TEntity extends object,
  TEditData,
  TEditVars,
  TDeleteData,
  TDeleteVars,
  TParent extends object,
> = EntityFields<TEntity, RequestBody<TEditVars>> & {
  /**
   * For entities found through another one (e.g. a comp set belongs to a hotel): the Entity Explorer
   * searches the parent `entity`, and selecting one lists this entity's items through
   * `endpoints.query`, with `setParentId` building the request that holds the parent id.
   */
  selector?: {
    entity: EntitySchema<TParent>;
    setParentId: (id: string) => TQueryArgs;
    /** Id of an item, unique within its parent; defaults to its position. */
    getId?: (item: NoInfer<TEntity>) => string | undefined;
  };
  endpoints: {
    /** The generated SDK `XYZQueryOptions` function. Omit when the API has no list endpoint. */
    query?: (
      args?: TQueryArgs
    ) => QueryExecuteOptions<
      EntityPage<TQueryItem>,
      ProblemDetail,
      EntityPage<TQueryItem>,
      EntityPage<TQueryItem>,
      TQueryKey
    >;
    /**
     * Turns one item of a `query` page into entities. Omit when each item is itself the entity;
     * e.g. an item holding a list of limits yields one entity per limit.
     */
    toItems?: (item: TQueryItem) => TEntity[];
    /**
     * Optional. Returns a `query` request scoped to an organization. Omit when the query cannot be
     * filtered by organization.
     */
    orgIdFilter?: (organizationId: string, request: NoInfer<TQueryArgs>) => NoInfer<TQueryArgs>;
    /** The generated SDK `XYZGetOptions` function. Omit when the API has no get-by-id. */
    read?: (args: {
      path: { id: string };
    }) => QueryExecuteOptions<TEntity, ProblemDetail, TEntity, TEntity, TReadKey>;
    edit?: EditEndpoint<TEditData, TEditVars, NoInfer<TEntity>>;
    delete?: DeleteEndpoint<TDeleteData, TDeleteVars, NoInfer<TEntity>>;
  };
};

/** Schema as consumed by the Entity Explorer; only the entity type remains generic. */
export type EntitySchema<TEntity extends object, TQueryArgs = never> = EntityFields<TEntity> & {
  /** Present when items are found through a parent entity. */
  selector?: {
    parentLabel: string;
    /** Searches the parents. */
    search: (params: EntityQueryParams) => Promise<ListPage>;
    /**
     * Lists this entity's items for the parent with the given id and/or within an organization.
     * Without a parent the query is unscoped by parent, so it needs an organization.
     */
    list: (parentId?: string, organizationId?: string) => Promise<ParentListEntry<TEntity>[]>;
    /** Whether `list` can be scoped to an organization. */
    supportsOrganization: boolean;
  };
  endpoints: {
    query?: (params: EntityQueryParams) => Promise<EntityPage<TEntity>>;
    /** Pages of entries for the Entity Explorer's list, from this entity's query. */
    list?: (params: EntityQueryParams) => Promise<ListPage>;
    read?: (id: string) => Promise<TEntity>;
    edit?: (id: string, key: string, value: EditableFieldValue) => Promise<unknown>;
    delete?: (id: string) => Promise<unknown>;
    deleteRelated?: (parentId: string, items: TEntity[], index: number) => Promise<unknown>;
    related?: (args: TQueryArgs) => Promise<TEntity[]>;
    editRelated?: {
      save: (parentId: string, items: TEntity[]) => Promise<unknown>;
      newItem: () => TEntity;
    };
  };
};

function entityText<TEntity extends object>(entity: TEntity, value: EntityText<TEntity>): string {
  if (typeof value === 'function') return value(entity);
  const raw: unknown = entity[value];
  return typeof raw === 'string' ? raw : '';
}

/** Reduces pages of `source` entities to list entries, texts taken from `display`. */
function toListEndpoint<TSource extends object>(
  query: (params: EntityQueryParams) => Promise<EntityPage<TSource>>,
  source: { idField?: keyof TSource & string },
  display: { primaryText: EntityText<TSource>; secondaryText?: EntityText<TSource> }
): (params: EntityQueryParams) => Promise<ListPage> {
  const { idField } = source;
  const text = entityText<TSource>;
  return async (params) => {
    const page = await query(params);
    return {
      nextPage: page.nextPage,
      entries: (page.results ?? []).flatMap((entity) => {
        const id: unknown = idField ? entity[idField] : undefined;
        if (typeof id !== 'string') return [];
        return [
          {
            id,
            primaryText: text(entity, display.primaryText),
            secondaryText: display.secondaryText ? text(entity, display.secondaryText) : undefined,
          },
        ];
      }),
    };
  };
}

export function defineEntitySchema<
  TQueryItem extends object,
  TQueryArgs extends QueryArgs,
  TQueryKey extends QueryKey,
  TReadKey extends QueryKey,
  TEntity extends object = TQueryItem,
  TEditData = never,
  TEditVars = never,
  TDeleteData = never,
  TDeleteVars = never,
  TParent extends object = object,
>(
  definition: EntitySchemaDefinition<
    TQueryItem,
    TQueryArgs,
    TQueryKey,
    TReadKey,
    TEntity,
    TEditData,
    TEditVars,
    TDeleteData,
    TDeleteVars,
    TParent
  >
): EntitySchema<TEntity, TQueryArgs> {
  const { endpoints, selector, ...rest } = definition;
  const { query, toItems, orgIdFilter, read, edit, delete: remove } = endpoints;
  // Without `toItems`, TEntity is TQueryItem (its default).
  const itemsOf = (page: EntityPage<TQueryItem>): TEntity[] =>
    (page.results ?? []).flatMap((item) => toItems?.(item) ?? [item as unknown as TEntity]);
  const runQuery = async (args: TQueryArgs): Promise<EntityPage<TEntity>> => {
    if (!query) throw new Error('No query endpoint.');
    const page = await queryClient.query(query(args));
    return { results: itemsOf(page), nextPage: page.nextPage };
  };
  const ownQuery =
    query &&
    (({ search, page, pageSize }: EntityQueryParams) =>
      runQuery({ query: { search, page, pageSize } } as TQueryArgs));
  const parentSearch = selector?.entity.endpoints.list;
  return {
    ...rest,
    selector:
      selector && query && parentSearch
        ? {
            parentLabel: selector.entity.label,
            search: parentSearch,
            supportsOrganization: Boolean(orgIdFilter),
            list: async (parentId, organizationId) => {
              const request =
                parentId === undefined ? ({} as TQueryArgs) : selector.setParentId(parentId);
              const page = await runQuery(
                organizationId && orgIdFilter ? orgIdFilter(organizationId, request) : request
              );
              const items = page.results ?? [];
              return items.map((item, index) => ({
                id: selector.getId?.(item) ?? String(index),
                primaryText: entityText(item, rest.primaryText),
                secondaryText: rest.secondaryText
                  ? entityText(item, rest.secondaryText)
                  : undefined,
                item,
              }));
            },
          }
        : undefined,
    endpoints: {
      list: ownQuery && toListEndpoint(ownQuery, rest, rest),
      query: ownQuery,
      read: read && ((id) => queryClient.query(read({ path: { id } }))),
      edit:
        edit &&
        ((id, key, value) => {
          const field = rest.fields.find((f) => f.key === key);
          if (!field?.convertValue || field.readOnly) {
            return Promise.reject(new Error(`Field "${key}" cannot be edited.`));
          }
          return runMutation(
            edit.mutation(),
            (edit.toRequest as (id: string, body: Partial<RequestBody<TEditVars>>) => TEditVars)(
              id,
              field.convertValue(value)
            )
          );
        }),
      delete:
        remove &&
        ((id) =>
          runMutation(remove.mutation(), (remove.toRequest as (id: string) => TDeleteVars)(id))),
      deleteRelated:
        remove &&
        ((parentId, items, index) =>
          runMutation(remove.mutation(), remove.toRequest(parentId, { items, index }))),
      related: query && (async (args) => (await runQuery(args)).results ?? []),
      editRelated: edit?.newItem && {
        newItem: edit.newItem,
        save: (parentId, items) =>
          runMutation(edit.mutation(), edit.toRequest(parentId, {}, { items })),
      },
    },
  };
}
